// Phase 36 Batch S — Emergent-managed Google Sign-In return handler.
//
// Flow:
//   1. /login or /signup → "Continue with Google" → redirect to
//      https://auth.emergentagent.com/?redirect=<app>/auth/callback
//   2. User signs in with Google on Emergent's hosted page.
//   3. Emergent redirects back here with #session_id=<token> in the
//      URL fragment.
//   4. We parse the fragment, POST it to /api/auth/google/session,
//      then either redirect to /dashboard (success) or back to /login
//      with an error toast (failure).
//
// ─── DEV STRICTMODE NOTE (Phase 36 Batch S hotfix) ────────────────────
//
// React's <StrictMode> intentionally double-mounts components in dev
// builds to surface side-effect issues. The naive implementation of
// this page wiped the URL fragment after reading it — Mount 1 would
// read the session_id, wipe the hash, and fire the backend exchange;
// then Mount 2 would re-run the effect, find an empty hash, and
// render "Missing session token" before Mount 1's success path got
// to navigate(). The user would see the failure screen even though
// the cookie had been set correctly.
//
// Two defences fix this without dropping StrictMode:
//   (a) Stash the session_id in sessionStorage IMMEDIATELY on first
//       read so subsequent mounts can recover it after the hash is
//       wiped.
//   (b) Coalesce the network exchange into a module-level in-flight
//       promise so BOTH mounts await the same single request (the
//       session_id is one-shot; a second backend call would 401 and
//       paint a misleading failure UI).
//
// Both defences are also useful in prod (StrictMode is dev-only but
// browser back/forward into a stale tab could otherwise re-fire the
// exchange).

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const STASH_KEY = "runidea_emergent_session_id";

// Module-level singleton — survives React StrictMode's intentional
// double-mount AND any future render re-runs. Holds the in-flight
// exchange promise so concurrent mounts share one result. Cleared
// after navigate() runs (or on hard failure).
let _inflightExchange = null;

function readSessionId() {
  // First try the URL fragment (this is where Emergent puts it).
  const hash = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : "";
  if (hash) {
    const fromHash = new URLSearchParams(hash).get("session_id");
    if (fromHash) return fromHash;
  }
  // Defensive: also check query string in case Emergent's contract
  // ever switches to ?session_id=… for some providers.
  const query = new URLSearchParams(window.location.search);
  const fromQuery = query.get("session_id");
  if (fromQuery) return fromQuery;
  // Last resort — a sibling mount (or this same mount post-hash-wipe)
  // may have stashed it before clearing the URL.
  try {
    return sessionStorage.getItem(STASH_KEY) || null;
  } catch {
    return null;
  }
}

async function exchangeSession(sessionId) {
  // Persist BEFORE wiping the URL so a StrictMode second mount (or a
  // hard refresh mid-exchange) can recover the value.
  try { sessionStorage.setItem(STASH_KEY, sessionId); } catch { /* ignore */ }
  // Wipe both the fragment and any query so a back-button or reload
  // doesn't re-submit a now-consumed token.
  try {
    window.history.replaceState(null, "", window.location.pathname);
  } catch { /* non-fatal */ }
  return api.post("/auth/google/session", { session_id: sessionId });
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth() || {};
  const [status, setStatus] = useState("processing");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sessionId = readSessionId();
      if (!sessionId) {
        if (cancelled) return;
        setStatus("error");
        setErrorMsg("Missing session token. Please try signing in again.");
        setTimeout(() => navigate("/login?error=missing_session", { replace: true }), 1500);
        return;
      }

      try {
        // Coalesce. Only ONE network call fires no matter how many
        // mounts (StrictMode runs the effect body twice in dev).
        if (!_inflightExchange) {
          _inflightExchange = exchangeSession(sessionId);
        }
        await _inflightExchange;
        // Refresh the auth context so the navbar + protected routes
        // see the new user without a hard reload.
        if (typeof refresh === "function") {
          try { await refresh(); } catch { /* refresh is best-effort */ }
        }
        // Recover any referral / share_token that was stashed before
        // the OAuth round-trip (Signup.jsx stashes it).
        try { sessionStorage.removeItem("runidea_share_token"); } catch { /* ignore */ }
        // Clean up the one-shot token + the in-flight slot.
        try { sessionStorage.removeItem(STASH_KEY); } catch { /* ignore */ }
        _inflightExchange = null;
        if (cancelled) return;
        setStatus("done");
        navigate("/dashboard", { replace: true });
      } catch (e) {
        // Clean up so the user can retry — leaving the in-flight
        // promise + sessionStorage entry around would make the next
        // attempt return the same stale rejection.
        _inflightExchange = null;
        try { sessionStorage.removeItem(STASH_KEY); } catch { /* ignore */ }
        if (cancelled) return;
        const detail = e?.response?.data?.detail
          || "Couldn't complete sign-in. Please try again.";
        setStatus("error");
        setErrorMsg(detail);
        setTimeout(() => navigate("/login?error=oauth_failed", { replace: true }), 2200);
      }
    })();
    return () => { cancelled = true; };
    // Run exactly once on mount — fragment handling is one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-12"
      data-testid="auth-callback-page"
    >
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
        <div className="flex flex-col items-center text-center">
          {status === "processing" || status === "done" ? (
            <>
              <Loader2 className="h-7 w-7 animate-spin text-indigo-600" />
              <h1 className="mt-4 text-lg font-semibold tracking-tight text-slate-900">
                {status === "done" ? "Signed in. Redirecting…" : "Completing sign-in…"}
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                Hang tight — this takes a second.
              </p>
            </>
          ) : (
            <>
              <AlertCircle className="h-7 w-7 text-rose-600" />
              <h1 className="mt-4 text-lg font-semibold tracking-tight text-slate-900">
                Sign-in failed
              </h1>
              <p className="mt-2 text-sm text-slate-500">{errorMsg}</p>
              <p className="mt-1 text-xs text-slate-400">Redirecting back to login…</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
